import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";

import { toast } from "sonner";

import api from "../../lib/api";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import AdminLayout from "../../components/admin/AdminLayout";
import BlogsTable from "../../components/admin/BlogsTable";

const isActiveValue = (blog) =>
  blog.is_active === true ||
  blog.is_active === 1 ||
  blog.is_active === "1" ||
  blog.is_active === "active";

const AdminBlog = () => {
  const navigate = useNavigate();

  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  // The blog pending deletion (drives the confirm dialog).
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const { data } = await api.get("/blogs");
        // Be tolerant of different response shapes (array, {data}, {blogs}).
        const list = Array.isArray(data)
          ? data
          : data?.data || data?.blogs || [];
        setBlogs(list);
      } catch (error) {
        // Table shows its empty state if this fails.
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  // Toggle is_active via the status API. Update optimistically, roll back on error.
  const handleToggleStatus = async (blog) => {
    const original = blog.is_active;
    const newStatus = isActiveValue(blog) ? 0 : 1;

    setBlogs((prev) =>
      prev.map((b) => (b.id === blog.id ? { ...b, is_active: newStatus } : b))
    );

    try {
      await api.post(`/blogs/${blog.id}/status`, { is_active: newStatus });
      toast.success("Status updated");
    } catch (error) {
      // Revert on failure.
      setBlogs((prev) =>
        prev.map((b) => (b.id === blog.id ? { ...b, is_active: original } : b))
      );
      toast.error(error.response?.data?.message || "Failed to update status");
    }
  };

  const handleEdit = (blog) => {
    navigate(`/admin/blog-edit/${blog.id}`);
  };

  // Confirm + delete via the API.
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.post(`/blogs/${deleteTarget.id}/delete`);
      // Soft delete — keep the row, just mark it deleted.
      setBlogs((prev) =>
        prev.map((b) =>
          b.id === deleteTarget.id
            ? { ...b, deleted_at: b.deleted_at || new Date().toISOString() }
            : b
        )
      );
      toast.success("Blog deleted");
      setDeleteTarget(null);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete blog");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <AdminLayout
      title="Blogs"
      actions={
        <Button
          onClick={() => navigate("/admin/blog-add")}
          className="gap-2 bg-[#b32126] hover:bg-[#8f1a1f]"
        >
          <Plus className="h-4 w-4" />
          Add Blog
        </Button>
      }
    >
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Blogs</h2>
        <p className="mt-1 text-sm text-gray-600">Manage your blog articles.</p>
      </div>

      <BlogsTable
        blogs={blogs}
        loading={loading}
        onToggleStatus={handleToggleStatus}
        onEdit={handleEdit}
        onDelete={(blog) => setDeleteTarget(blog)}
      />

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && !deleting && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this blog?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete{" "}
              <span className="font-medium text-gray-900">
                “{deleteTarget?.blog_title || deleteTarget?.title || "this blog"}”
              </span>
              . This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                confirmDelete();
              }}
              disabled={deleting}
              className="bg-[#b32126] hover:bg-[#8f1a1f]"
            >
              {deleting ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
};

export default AdminBlog;
